<template>
  <div class="landing container">
    <!-- Hero -->
    <section
      id="hero"
      class="hero card"
    >
      <div class="hero-inner">
        <div class="hero-text">
          <div class="hero-slogan">
            <span class="hero-slogan-zh">你我皆盘友</span>
            <span class="hero-slogan-dot">·</span>
            <span class="hero-slogan-en">Connect Disc Golfers</span>
          </div>
          <h1 class="hero-title">
            全国飞盘高尔夫运动平台
          </h1>
          <div class="hero-sub">
            一站式赛事 · 场地 · 俱乐部 · 会员服务
          </div>
          <div class="hero-actions">
            <a
              class="btn"
              href="#events"
              @click.prevent="scrollTo('events')"
            >
              最新赛事
            </a>
            <a
              class="btn secondary"
              href="#players"
              @click.prevent="scrollTo('players')"
            >
              会员排行
            </a>
          </div>
          <div class="hero-meta">
            <span class="hero-meta-label">支持单位</span>
            <a
              class="hero-meta-pdga"
              href="https://www.pdga.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="pdga-mark">PDGA</span>
              <span class="pdga-name">Professional Disc Golf Association</span>
            </a>
          </div>
        </div>
        <div
          class="hero-qr clickable"
          role="button"
          tabindex="0"
          @click="openQr('CDGC 小程序')"
          @keyup.enter="openQr('CDGC 小程序')"
        >
          <img
            src="/qrcode.jpg"
            alt="小程序二维码"
            class="hero-qr-img"
            @error="onImgError($event)"
          >
          <div class="hero-qr-tip">
            扫码进入小程序
          </div>
        </div>
      </div>
    </section>

    <!-- 会员排行：左右双栏（男子 / 女子）各前 10 -->
    <section
      id="players"
      class="section"
    >
      <div class="section-head">
        <h2 class="section-title">
          会员排行
        </h2>
        <button
          class="more-btn"
          @click="openQr('查看更多会员')"
        >
          查看更多
        </button>
      </div>
      <div
        v-if="loading.players"
        class="state"
      >
        加载中...
      </div>
      <div
        v-else
        class="player-split"
      >
        <!-- 男子 -->
        <div class="player-col">
          <div class="player-col-title col-male">
            <span class="col-icon">♂</span>男子
          </div>
          <div
            v-if="!playersMale.length"
            class="state state-mini"
          >
            暂无数据
          </div>
          <div
            v-else
            class="player-list"
          >
            <div
              v-for="(p, idx) in playersMale"
              :key="p.id"
              class="player-row clickable"
              :class="{ 'rank-1-row': idx === 0 }"
              :style="idx === 0 && p.avatarUrl ? `--rank-bg: url(${p.avatarUrl})` : null"
              role="button"
              tabindex="0"
              @click="openQr(p.name)"
              @keyup.enter="openQr(p.name)"
            >
              <div
                class="player-rank"
                :class="rankClass(idx)"
              >
                {{ idx + 1 }}
              </div>
              <div class="player-avatar">
                <img
                  v-if="p.avatarUrl"
                  :src="p.avatarUrl"
                  :alt="p.name"
                  class="player-avatar-img"
                  loading="lazy"
                  @error="onImgError($event)"
                >
                <div
                  v-else
                  class="player-avatar-placeholder"
                >
                  {{ avatarLetter(p.name) }}
                </div>
              </div>
              <div class="player-info">
                <div class="item-title">
                  {{ p.name }}
                </div>
                <div
                  v-if="p.club && p.club.name"
                  class="item-meta club-line"
                >
                  {{ p.club.name }}
                </div>
                <div class="player-numbers">
                  <span
                    v-if="p.membershipNumber"
                    class="num-chip"
                  >CDGC #{{ p.membershipNumber }}</span>
                  <span
                    v-if="p.pdgaNumber"
                    class="num-chip pdga"
                  >PDGA #{{ p.pdgaNumber }}</span>
                </div>
              </div>
              <div class="player-rating">
                {{ p.currentRating != null ? Number(p.currentRating).toFixed(0) : '—' }}
              </div>
            </div>
          </div>
        </div>

        <!-- 女子 -->
        <div class="player-col">
          <div class="player-col-title col-female">
            <span class="col-icon">♀</span>女子
          </div>
          <div
            v-if="!playersFemale.length"
            class="state state-mini"
          >
            暂无数据
          </div>
          <div
            v-else
            class="player-list"
          >
            <div
              v-for="(p, idx) in playersFemale"
              :key="p.id"
              class="player-row clickable"
              :class="{ 'rank-1-row': idx === 0 }"
              :style="idx === 0 && p.avatarUrl ? `--rank-bg: url(${p.avatarUrl})` : null"
              role="button"
              tabindex="0"
              @click="openQr(p.name)"
              @keyup.enter="openQr(p.name)"
            >
              <div
                class="player-rank"
                :class="rankClass(idx)"
              >
                {{ idx + 1 }}
              </div>
              <div class="player-avatar">
                <img
                  v-if="p.avatarUrl"
                  :src="p.avatarUrl"
                  :alt="p.name"
                  class="player-avatar-img"
                  loading="lazy"
                  @error="onImgError($event)"
                >
                <div
                  v-else
                  class="player-avatar-placeholder"
                >
                  {{ avatarLetter(p.name) }}
                </div>
              </div>
              <div class="player-info">
                <div class="item-title">
                  {{ p.name }}
                </div>
                <div
                  v-if="p.club && p.club.name"
                  class="item-meta club-line"
                >
                  {{ p.club.name }}
                </div>
                <div class="player-numbers">
                  <span
                    v-if="p.membershipNumber"
                    class="num-chip"
                  >CDGC #{{ p.membershipNumber }}</span>
                  <span
                    v-if="p.pdgaNumber"
                    class="num-chip pdga"
                  >PDGA #{{ p.pdgaNumber }}</span>
                </div>
              </div>
              <div class="player-rating">
                {{ p.currentRating != null ? Number(p.currentRating).toFixed(0) : '—' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 赛事 -->
    <section
      id="events"
      class="section"
    >
      <div class="section-head">
        <h2 class="section-title">
          最新赛事（C-Tier 及以上）
        </h2>
        <button
          class="more-btn"
          @click="openQr('查看更多赛事')"
        >
          查看更多
        </button>
      </div>
      <div
        v-if="loading.events"
        class="state"
      >
        加载中...
      </div>
      <div
        v-else-if="!events.length"
        class="state"
      >
        暂无赛事
      </div>
      <div
        v-else
        class="grid cols-3 event-grid"
      >
        <div
          v-for="e in events"
          :key="e.id"
          class="item card clickable event-item"
          role="button"
          tabindex="0"
          @click="openQr(e.name)"
          @keyup.enter="openQr(e.name)"
        >
          <div class="event-cover">
            <img
              v-if="e.img"
              :src="e.img"
              :alt="e.name"
              class="event-cover-img"
              loading="lazy"
              @error="onImgError($event)"
            >
            <div
              v-else
              class="event-cover-placeholder"
            >
              暂无图片
            </div>
            <span
              v-if="e.tier && e.tier.name"
              class="event-tier"
              :style="tierStyle(e.tier)"
            >{{ e.tier.name }}</span>
          </div>
          <div class="event-body">
            <div class="item-title">
              {{ e.name }}
            </div>
            <div class="item-meta">
              {{ formatRange(e.startTime, e.endTime) }}
            </div>
            <div class="item-meta">
              {{ e.city }} {{ e.address }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 场地 -->
    <section
      id="courses"
      class="section courses-section"
    >
      <div class="section-head">
        <h2 class="section-title">
          最新盘场
        </h2>
        <button
          class="more-btn"
          @click="openQr('查看更多场地')"
        >
          查看更多
        </button>
      </div>
      <div
        v-if="loading.courses"
        class="state"
      >
        加载中...
      </div>
      <div
        v-else-if="!courses.length"
        class="state"
      >
        暂无场地
      </div>
      <div
        v-else
        class="grid cols-3 course-grid"
      >
        <div
          v-for="c in courses"
          :key="c.id"
          class="item card clickable course-item"
          role="button"
          tabindex="0"
          @click="openQr(c.name)"
          @keyup.enter="openQr(c.name)"
        >
          <div class="cover">
            <img
              v-if="c.imgUrl"
              :src="c.imgUrl"
              :alt="c.name"
              class="cover-img"
              loading="lazy"
              @error="onImgError($event)"
            >
            <div
              v-else
              class="cover-placeholder"
            >
              暂无图片
            </div>
            <span
              v-if="c.holesNum"
              class="cover-tag"
            >{{ c.holesNum }} 洞</span>
          </div>
          <div class="cover-body">
            <div class="item-title">
              {{ c.name }}
            </div>
            <div class="item-meta">
              {{ c.city }} {{ c.address }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 俱乐部 -->
    <section
      id="clubs"
      class="section"
    >
      <div class="section-head">
        <h2 class="section-title">
          最新俱乐部
        </h2>
        <button
          class="more-btn"
          @click="openQr('查看更多俱乐部')"
        >
          查看更多
        </button>
      </div>
      <div
        v-if="loading.clubs"
        class="state"
      >
        加载中...
      </div>
      <div
        v-else-if="!clubs.length"
        class="state"
      >
        暂无俱乐部
      </div>
      <div
        v-else
        class="grid cols-3 club-grid"
      >
        <div
          v-for="c in clubs"
          :key="c.id"
          class="item card clickable club-item"
          role="button"
          tabindex="0"
          @click="openQr(c.name)"
          @keyup.enter="openQr(c.name)"
        >
          <div class="cover">
            <img
              v-if="clubCover(c)"
              :src="clubCover(c)"
              :alt="c.name"
              class="cover-img"
              loading="lazy"
              @error="onImgError($event)"
            >
            <div
              v-else
              class="cover-placeholder"
            >
              {{ clubInitial(c.name) }}
            </div>
          </div>
          <div class="cover-body">
            <div class="item-title">
              {{ c.name }}
            </div>
            <div class="item-meta">
              {{ c.city }} {{ c.address }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 公园设计盘场 -->
    <section
      id="course-design"
      class="section"
    >
      <div class="design-card">
        <div class="design-text">
          <h2 class="design-title">
            公园设计盘场
          </h2>
          <p class="design-desc">
            为城市公园、社区绿地、景区定制专业的飞盘高尔夫场地规划与设计服务，
            从选址勘察、布局设计到施工落地的全流程支持。
          </p>
        </div>
        <a
          class="design-contact"
          href="mailto:info@theCDGC.com"
        >
          <span class="design-contact-label">联系我们</span>
          <span class="design-contact-email">info@theCDGC.com</span>
        </a>
      </div>
    </section>
  </div>
</template>

<script>
import {
  listEvents,
  listCourses,
  listClubs,
  listPlayers,
} from '../api/webApi';
import { eventBus } from '../eventBus';

// C-Tier 及以上：tierId <= 5
const MIN_TIER_ID = 5;

// 简易颜色亮度判断：背景浅则用深字，深则用白字
function isLightColor(color) {
  if (!color) return true;
  let hex = String(color).trim();
  // 去掉 # / rgb() 包装
  if (hex.startsWith('#')) hex = hex.slice(1);
  if (hex.startsWith('rgb')) {
    const m = hex.match(/\d+/g);
    if (!m || m.length < 3) return true;
    hex = [m[0], m[1], m[2]]
      .map((n) => Number(n).toString(16).padStart(2, '0'))
      .join('');
  }
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return true;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // YIQ 亮度
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160;
}

// 判断俱乐部是否有封面图（logo 或 images）
function hasClubCover(c) {
  if (!c) return false;
  if (c.logo) return true;
  if (Array.isArray(c.images) && c.images.length) {
    return c.images.some(
      (img) => img && (img.url || img.path || img.imgUrl || img.src)
    );
  }
  return false;
}

// 现在后端 list 接口已按性别过滤并返回前 10 名有效会员，
// 前端不再需要 gender 字段 normalize 工具函数。

export default {
  name: 'Home',
  data() {
    return {
      events: [],
      courses: [],
      clubs: [],
      playersMale: [],
      playersFemale: [],
      loading: {
        events: true,
        courses: true,
        clubs: true,
        players: true,
      },
    };
  },
  async created() {
    // 各 section 独立加载，单个失败不影响其他 section
    await Promise.all([
      this.loadEvents(),
      this.loadCourses(),
      this.loadClubs(),
      this.loadPlayers(),
    ]);

    // 处理从旧路由 /events#/xxx 这种带 hash 的跳转
    this.$nextTick(() => this.handleHash());
  },
  mounted() {
    // 在 head 里注入动态结构化数据（事件/场地/俱乐部），
    // 让搜索引擎能在 SPA 渲染时也抓到这些实体信息
    this.$nextTick(() => {
      this._refreshStructuredData();
    });
  },
  beforeDestroy() {
    if (this._seoScript) {
      this._seoScript.parentNode && this._seoScript.parentNode.removeChild(this._seoScript);
      this._seoScript = null;
    }
  },
  methods: {
    async loadEvents() {
      try {
        // 多取一些再前端过滤，避免后端没支持 tierId 参数时分页浪费
        const data = await listEvents({ currentPage: 1, pageSize: 30 });
        const list = (data && data.list) || [];
        this.events = list
          .filter((e) => e.tierId != null && Number(e.tierId) <= MIN_TIER_ID)
          .slice(0, 6);
      } catch (e) {
        console.warn('加载赛事失败', e);
        this.events = [];
      } finally {
        this.loading.events = false;
        this.$nextTick(() => this._refreshStructuredData());
      }
    },
    async loadCourses() {
      try {
        // 多取一些，前端过滤掉没有封面图的
        const data = await listCourses({ currentPage: 1, pageSize: 30 });
        const list = (data && data.list) || [];
        this.courses = list.filter((c) => !!c.imgUrl).slice(0, 6);
      } catch (e) {
        console.warn('加载场地失败', e);
        this.courses = [];
      } finally {
        this.loading.courses = false;
        this.$nextTick(() => this._refreshStructuredData());
      }
    },
    async loadClubs() {
      try {
        // 多取一些，前端过滤掉没有 logo 或 images 的
        const data = await listClubs({ currentPage: 1, pageSize: 30 });
        const list = (data && data.list) || [];
        this.clubs = list.filter((c) => hasClubCover(c)).slice(0, 6);
      } catch (e) {
        console.warn('加载俱乐部失败', e);
        this.clubs = [];
      } finally {
        this.loading.clubs = false;
        this.$nextTick(() => this._refreshStructuredData());
      }
    },
    async loadPlayers() {
      // 一次并发拉两个列表：男子前 10 + 女子前 10
      try {
        const [maleData, femaleData] = await Promise.all([
          listPlayers({ gender: 'male' }),
          listPlayers({ gender: 'female' }),
        ]);
        this.playersMale = (maleData && maleData.list) || [];
        this.playersFemale = (femaleData && femaleData.list) || [];
      } catch (e) {
        console.warn('加载会员失败', e);
        this.playersMale = [];
        this.playersFemale = [];
      } finally {
        this.loading.players = false;
        // 数据到位后刷新结构化数据
        this.$nextTick(() => this._refreshStructuredData());
      }
    },
    _refreshStructuredData() {
      const base = 'https://www.thecdgc.com';
      const graph = [];

      // 赛事（Event）
      (this.events || []).forEach((e) => {
        const ld = {
          '@type': 'Event',
          name: e.name,
          startDate: e.startTime || undefined,
          endDate: e.endTime || undefined,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          sport: 'Disc Golf',
          location: {
            '@type': 'Place',
            name: e.city || e.address || '',
            address: [e.city, e.address].filter(Boolean).join(' '),
          },
        };
        if (e.img) ld.image = e.img;
        if (e.tier && e.tier.name) {
          ld.description = `${e.tier.name} 级飞盘高尔夫赛事`;
        }
        graph.push(ld);
      });

      // 场地（SportsActivityLocation）
      (this.courses || []).forEach((c) => {
        const ld = {
          '@type': 'SportsActivityLocation',
          name: c.name,
          sport: 'Disc Golf',
          address: {
            '@type': 'PostalAddress',
            addressLocality: c.city || '',
            streetAddress: c.address || '',
          },
        };
        if (c.imgUrl) ld.image = c.imgUrl;
        if (c.holesNum) ld.description = `${c.holesNum} 洞飞盘高尔夫场地`;
        graph.push(ld);
      });

      // 俱乐部（SportsClub）
      (this.clubs || []).forEach((c) => {
        const ld = {
          '@type': 'SportsClub',
          name: c.name,
          sport: 'Disc Golf',
          address: {
            '@type': 'PostalAddress',
            addressLocality: c.city || '',
            streetAddress: c.address || '',
          },
          url: `${base}/`,
        };
        if (c.logo) ld.logo = c.logo;
        graph.push(ld);
      });

      if (!graph.length) return;

      const json = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': graph,
      });

      if (this._seoScript) {
        this._seoScript.textContent = json;
      } else {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-cdgc-dynamic', '1');
        s.textContent = json;
        document.head.appendChild(s);
        this._seoScript = s;
      }
    },
    avatarLetter(name) {
      if (!name) return '?';
      return String(name).trim().charAt(0).toUpperCase();
    },
    rankClass(idx) {
      if (idx === 0) return 'rank-1';
      if (idx === 1) return 'rank-2';
      if (idx === 2) return 'rank-3';
      return '';
    },
    // 俱乐部封面：优先 logo，其次关联 images 第一张
    clubCover(c) {
      if (!c) return '';
      if (c.logo) return c.logo;
      if (Array.isArray(c.images) && c.images.length) {
        const first = c.images.find(
          (img) => img && (img.url || img.path || img.imgUrl || img.src)
        );
        if (first) return first.url || first.path || first.imgUrl || first.src;
      }
      return '';
    },
    clubInitial(name) {
      if (!name) return '?';
      return String(name).trim().charAt(0).toUpperCase();
    },
    formatRange(s, e) {
      if (!s || !e) return '';
      return `${String(s).slice(0, 16)} - ${String(e).slice(11, 16)}`;
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    openQr(title) {
      // 通过 eventBus 触发 App.vue 里监听的 show-qr 事件
      eventBus.$emit('show-qr', title);
    },
    handleHash() {
      const hash = window.location.hash.replace('#', '');
      if (hash) this.scrollTo(hash);
    },
    // 图片 404 时隐藏，避免破图占位
    onImgError(ev) {
      if (ev && ev.target) {
        ev.target.style.display = 'none';
      }
    },
    // 根据 tier 的 bgColor 生成 tag 样式；没颜色时用默认半透明黑底
    tierStyle(tier) {
      if (!tier) return {};
      const bg = tier.bgColor || tier.bg_color;
      if (!bg) return {};
      // 简单判断：浅色用深字，深色用白字
      const text = isLightColor(bg) ? '#0f172a' : '#fff';
      return {
        background: bg,
        color: text,
        backdropFilter: 'none',
      };
    },
  },
};
</script>

<style scoped>
.hero {
  border-radius: 24px;
  overflow: hidden;
  min-height: 280px;
  background:
    radial-gradient(1200px 500px at 20% 10%, rgba(0, 190, 190, 0.22), transparent 60%),
    radial-gradient(1200px 500px at 80% 0%, rgba(0, 190, 190, 0.16), transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(248, 250, 252, 1));
}
.hero-inner {
  padding: 44px 32px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: center;
  min-height: 280px;
}
@media (min-width: 720px) {
  .hero-inner {
    grid-template-columns: 1fr auto;
    gap: 32px;
  }
}
.hero-text {
  min-width: 0;
}
.hero-qr {
  display: none;
}
@media (min-width: 720px) {
  .hero-qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #fff;
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: 0 4px 16px rgba(0, 190, 190, 0.1);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .hero-qr:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 190, 190, 0.18);
  }
}
.hero-qr-img {
  width: 150px;
  height: 150px;
  object-fit: contain;
  display: block;
}
.hero-qr-tip {
  font-size: 12px;
  color: var(--muted);
}
.hero-title {
  margin: 0;
  font-size: 24px;
  letter-spacing: 0.2px;
}
@media (min-width: 720px) {
  .hero-title {
    font-size: 32px;
  }
}
.hero-slogan {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 190, 190, 0.1);
  border: 1px solid rgba(0, 190, 190, 0.25);
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-600, #008c8c);
  letter-spacing: 0.3px;
  width: fit-content;
}
.hero-slogan-dot {
  opacity: 0.5;
  font-weight: 400;
}
.hero-slogan-en {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: 0.5px;
}
.hero-sub {
  margin-top: 10px;
  color: var(--muted);
}
.hero-actions {
  margin-top: 18px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.hero-meta {
  margin-top: 22px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 13px;
  color: var(--muted);
}
.hero-meta-label {
  position: relative;
  padding-left: 10px;
}
.hero-meta-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background: var(--primary);
}
.hero-meta-pdga {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  color: var(--text);
  text-decoration: none;
  font-weight: 600;
  transition: background 0.15s;
}
.hero-meta-pdga:hover {
  background: rgba(0, 190, 190, 0.12);
}
.pdga-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 6px;
  background: #0f172a;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}
.pdga-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted);
}
.section {
  margin-top: 28px;
  scroll-margin-top: 80px; /* 锚点滚动时给 sticky header 留位置 */
}
.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
}
.section-title {
  margin: 0;
  font-size: 18px;
}
.state {
  padding: 18px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}
.item {
  padding: 14px;
  border-radius: 16px;
}
.item.clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}
.item.clickable:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 16px rgba(0, 190, 190, 0.12);
  transform: translateY(-2px);
}
.item.clickable:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.item-title {
  font-weight: 700;
  line-height: 1.3;
}
.item-meta {
  margin-top: 8px;
  font-size: 13px;
  color: var(--muted);
}

/* 赛事卡片：上图下文 */
.event-item {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.event-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  overflow: hidden;
}
.event-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.event-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 13px;
}
.event-tier {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.65);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}
.event-body {
  padding: 12px 14px 14px;
}

/* 性别切换 */
.gender-toggle {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
}
.toggle-btn {
  border: none;
  background: transparent;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.toggle-btn:hover {
  color: var(--text);
}
.toggle-btn.active {
  background: #fff;
  color: var(--text);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* 会员排行：左右双栏 */
.player-split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 880px) {
  .player-split {
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
}
.player-col {
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 8px 8px 12px;
  position: relative;
  overflow: hidden;
}
.player-col::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
}
.player-col-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.col-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
}
.player-col.col-male::before {
  background: linear-gradient(90deg, #2563eb, #3b82f6);
}
.player-col.col-male .col-icon {
  background: #2563eb;
}
.player-col.col-male .player-rank.rank-1 {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
}
.player-col.col-female::before {
  background: linear-gradient(90deg, #db2777, #ec4899);
}
.player-col.col-female .col-icon {
  background: #db2777;
}
.player-col.col-female .player-rank.rank-1 {
  background: linear-gradient(135deg, #db2777, #ec4899);
}
.state-mini {
  padding: 16px;
  font-size: 13px;
}
.club-line {
  font-weight: 600;
  color: var(--primary-600, #008c8c);
}

/* 会员排行 */
.player-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px;
}
.player-row {
  display: grid;
  grid-template-columns: 36px 44px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;
  background-color: transparent;
  position: relative;
  isolation: isolate;
}
.player-row:hover {
  background-color: rgba(0, 190, 190, 0.06);
}
/* 第一名：用头像做淡淡背景 */
.player-row.rank-1-row {
  background-color: transparent;
}
.player-row.rank-1-row::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.88);
  pointer-events: none;
  z-index: 0;
  border-radius: 12px;
}
.player-row.rank-1-row::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--rank-bg, none);
  background-repeat: no-repeat;
  background-position: top center;
  background-size: cover;
  opacity: 0.22;
  pointer-events: none;
  z-index: 1;
  border-radius: 12px;
}
.player-row.rank-1-row > * {
  position: relative;
  z-index: 2;
}
.player-row.rank-1-row::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.97) 0%,
    rgba(255, 255, 255, 0.92) 45%,
    rgba(255, 255, 255, 0.7) 75%,
    rgba(255, 255, 255, 0.82) 100%
  );
  z-index: -1;
  pointer-events: none;
}
.player-row.rank-1-row:hover::before {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.85) 45%,
    rgba(255, 255, 255, 0.55) 75%,
    rgba(255, 255, 255, 0.7) 100%
  );
}
/* 保证第一名内容浮在背景之上 */
.player-row.rank-1-row > * {
  position: relative;
}
.player-rank {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
}
.player-rank.rank-1 {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #fff;
}
.player-rank.rank-2 {
  background: linear-gradient(135deg, #cbd5e1, #94a3b8);
  color: #fff;
}
.player-rank.rank-3 {
  background: linear-gradient(135deg, #fb923c, #ea580c);
  color: #fff;
}
.player-avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  overflow: hidden;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.player-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.player-avatar-placeholder {
  color: var(--muted);
  font-weight: 700;
  font-size: 16px;
}
.player-info {
  min-width: 0;
}
.player-info .item-title {
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.player-info .item-meta {
  margin-top: 2px;
}
.player-numbers {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.num-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 190, 190, 0.1);
  color: var(--primary-600, #008c8c);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.num-chip.pdga {
  background: rgba(15, 23, 42, 0.06);
  color: var(--text);
}
.player-rating {
  font-weight: 800;
  font-size: 16px;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* 场地/俱乐部 卡片：上图下文 */
.course-item,
.club-item {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cover {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  overflow: hidden;
}
.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 14px;
  font-weight: 700;
}
.cover-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
}
.cover-body {
  padding: 12px 14px 14px;
}

/* 俱乐部在手机端展示为两列 */
.club-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (min-width: 768px) {
  .club-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
}

/* 手机端赛事/场地/俱乐部 grid 两侧留白 */
@media (max-width: 767px) {
  .event-grid,
  .course-grid,
  .club-grid {
    margin-left: 12px;
    margin-right: 12px;
  }
}

/* 最新盘场 section 在手机端让 card 整体往里收，两侧留白 */
@media (max-width: 767px) {
  .courses-section {
    padding-left: 14px;
    padding-right: 14px;
  }
  .courses-section .course-grid {
    margin-left: 0;
    margin-right: 0;
  }
}

/* 手机端所有 section 标题两侧留白 */
@media (max-width: 767px) {
  .section-head {
    padding-left: 14px;
    padding-right: 14px;
  }
}

/* 查看更多 */
.more-btn {
  border: none;
  background: rgba(0, 190, 190, 0.08);
  color: var(--primary-600, #008c8c);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 999px;
  transition: background 0.15s, transform 0.15s;
}
.more-btn:hover {
  background: rgba(0, 190, 190, 0.18);
  transform: translateY(-1px);
}
.more-btn:active {
  transform: translateY(0);
}

/* 公园设计盘场联系卡 */
.design-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: linear-gradient(
    135deg,
    rgba(0, 190, 190, 0.08) 0%,
    rgba(0, 190, 190, 0.18) 100%
  );
  border: 1px solid rgba(0, 190, 190, 0.2);
  border-radius: 16px;
  padding: 20px 22px;
}
.design-text {
  flex: 1 1 320px;
  min-width: 0;
}
.design-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
}
.design-desc {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}
.design-contact {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 18px;
  background: var(--primary);
  color: #fff;
  border-radius: 14px;
  text-decoration: none;
  transition: transform 0.15s, box-shadow 0.15s;
  flex-shrink: 0;
}
.design-contact:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 190, 190, 0.28);
}
.design-contact-label {
  font-size: 12px;
  opacity: 0.9;
}
.design-contact-email {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
}
</style>